import React from "react";
import { Outlet, useNavigate, Navigate } from "react-router-dom";
import { SignOut, ArrowSquareOut } from "@phosphor-icons/react";
import { OtmLogo } from "@/components/common/OtmLogo";
import { ASSETS } from "@/lib/assets";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { adminApi, ADMIN_TOKEN_KEY } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { AdminNav } from "@/components/admin/nav/AdminNav";

export default function AdminLayout() {
  const { isAuthed, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [pending, setPending] = React.useState(0);
  const [discrepancies, setDiscrepancies] = React.useState(0);
  const [tickets, setTickets] = React.useState(0);

  const refreshBadges = React.useCallback(() => {
    if (!isAuthed) return;
    adminApi.get("/admin/repairer-submissions/count").then(({ data }) => setPending(data.pending || 0)).catch(() => {});
    adminApi.get("/admin/discrepancies/count").then(({ data }) => setDiscrepancies(data.open || 0)).catch(() => {});
    adminApi.get("/admin/support-tickets/count").then(({ data }) => setTickets(data.open || 0)).catch(() => {});
  }, [isAuthed]);

  usePolling(refreshBadges, 30000);

  React.useEffect(() => {
    window.addEventListener("otm:badges-refresh", refreshBadges);
    return () => window.removeEventListener("otm:badges-refresh", refreshBadges);
  }, [refreshBadges]);

  React.useEffect(() => {
    refreshBadges();
  }, [refreshBadges]);

  if (!isAuthed) {
    const stale = !!localStorage.getItem(ADMIN_TOKEN_KEY);
    return <Navigate to={stale ? "/admin/login?expired=1" : "/admin/login"} replace />;
  }

  return (
    <div className="h-screen w-full flex bg-[#050505] text-neutral-100 overflow-hidden">
      <aside className="w-64 shrink-0 bg-[#0F0F0F] border-r border-neutral-800 flex flex-col">
        <div className="px-5 py-6 border-b border-neutral-800 flex flex-col items-center text-center" data-testid="admin-brand">
          <OtmLogo className="items-center" imgClassName="h-7 !object-center" showCaravans={false} />
          <div className="text-[0.6rem] font-bold tracking-[0.3em] text-[#73bf44] mt-2 pl-[0.3em]">TECH AI · ADMIN</div>
        </div>
        <AdminNav
          badges={{
            "/admin/submissions": { count: pending, tone: "green", testId: "submissions-badge" },
            "/admin/support-tickets": { count: tickets, tone: "amber", testId: "support-tickets-badge" },
            "/admin/discrepancies": { count: discrepancies, tone: "red", testId: "discrepancies-badge" },
          }}
        />
        <div className="p-3 border-t border-neutral-800 space-y-1">
          <button
            onClick={() => navigate("/")}
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-neutral-400 hover:text-white hover:bg-white/[0.04] transition-colors"
            data-testid="admin-view-site"
          >
            <ArrowSquareOut size={18} /> View Customer Site
          </button>
          <button
            onClick={() => { logout(); navigate("/admin/login"); }}
            className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
            data-testid="admin-logout"
          >
            <SignOut size={18} /> Sign Out
          </button>
        </div>
      </aside>

      <div className="relative flex-1 min-w-0">
        <div aria-hidden data-testid="admin-backdrop" className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-[0.03] grayscale"
          style={{ backgroundImage: `url("${ASSETS.adminBackdrop}")` }} />
        <main className="relative h-full overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
